/**
 * Match Chat — pre-transfer Q&A between a learner and an interested ADI.
 *
 * Shipped for Phase 4 with *static* suggested questions (AI assist later).
 * One thread per (transfer_request_id, adi_id). Learner can have several
 * chats open at once (one per interested ADI); once they pick one via the
 * Transfer Hub "Continue with them" button, every OTHER chat flips to
 * 'declined' status (read-only, calm tone — not "rejected").
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, Lightbulb, Lock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Bubble({ msg, mine }) {
  return (
    <div
      data-testid={`chat-bubble-${msg.id}`}
      className={`flex ${mine ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm ${
          mine
            ? "bg-indigo-600 text-white rounded-br-sm"
            : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
        }`}
      >
        {!mine && (
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
            {msg.sender_name}
          </p>
        )}
        <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.body}</p>
        <p
          className={`text-[10px] mt-1 ${mine ? "text-indigo-200" : "text-slate-400"}`}
        >
          {new Date(msg.sent_at).toLocaleString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
}

export default function TransferMatchChat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [composer, setComposer] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API_URL}/api/transfer-network/chats/${encodeURIComponent(chatId)}`,
        { withCredentials: true },
      );
      setData(d);
      const audience = d.viewer_side === "adi" ? "adi" : "learner";
      try {
        const { data: s } = await axios.get(
          `${API_URL}/api/transfer-network/chats/suggested-questions?audience=${audience}`,
          { withCredentials: true },
        );
        setSuggestions(s?.questions || []);
      } catch {
        setSuggestions([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chat not found");
      goBack();
    } finally {
      setLoading(false);
    }
  }, [chatId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll to latest on message change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [data?.messages?.length]);

  const send = async (text) => {
    const body = (text ?? composer).trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/transfer-network/chats/${encodeURIComponent(chatId)}/messages`,
        { body },
        { withCredentials: true },
      );
      setComposer("");
      setShowSuggestions(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-slate-400">Loading chat…</p>
      </div>
    );
  }
  if (!data) return null;
  const { chat, messages, viewer_side } = data;
  const otherName = viewer_side === "adi"
    ? (chat.learner_name || "Your new learner")
    : (chat.adi_name || "Instructor");
  const closed = chat.status === "declined";
  const selected = chat.status === "selected";

  return (
    <div data-testid="match-chat-page" className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100"
            data-testid="match-chat-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-slate-900 truncate">{otherName}</h1>
            <p className="text-[11px] text-slate-500">
              {selected ? "Selected · Transfer Hub is now open" : closed ? "This chat is now read-only" : "Match Chat"}
            </p>
          </div>
          {selected && (
            <button
              type="button"
              onClick={() => navigate(`/learn/transfer-hub/${chat.transfer_request_id}`)}
              data-testid="match-chat-open-hub"
              className="text-xs font-bold bg-emerald-600 text-white rounded-lg px-3 py-1.5 hover:bg-emerald-700"
            >
              Open Transfer Hub
            </button>
          )}
        </div>
      </div>

      {/* Message list */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto max-w-3xl w-full mx-auto px-4 py-4 space-y-2"
        data-testid="match-chat-message-list"
      >
        {messages.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-slate-500">No messages yet — say hello!</p>
            <p className="text-[11px] text-slate-400 mt-1">
              Your message history stays private to the two of you.
            </p>
          </div>
        ) : (
          messages.map((m) => (
            <Bubble
              key={m.id}
              msg={m}
              mine={m.sender_user_id && data.viewer_side !== "admin" &&
                ((data.viewer_side === "learner" && m.sent_by === "learner") ||
                 (data.viewer_side === "adi" && m.sent_by === "adi"))}
            />
          ))
        )}
      </div>

      {/* Suggested-question tray */}
      {!closed && showSuggestions && suggestions.length > 0 && (
        <div className="max-w-3xl w-full mx-auto px-4 pb-2">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" /> Suggested questions
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((q, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => send(q)}
                  data-testid={`chat-suggestion-${idx}`}
                  className="text-[11px] font-semibold bg-white border border-amber-300 text-amber-900 rounded-full px-2.5 py-1 hover:bg-amber-100"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="bg-white border-t border-slate-200 sticky bottom-0">
        <div className="max-w-3xl mx-auto px-4 py-3">
          {closed ? (
            <div
              data-testid="match-chat-closed-banner"
              className="flex items-center gap-2 text-xs text-slate-500 justify-center py-2"
            >
              <Lock className="w-3.5 h-3.5" />
              This chat is closed — the learner chose a different instructor.
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => setShowSuggestions((s) => !s)}
                data-testid="match-chat-toggle-suggestions"
                className={`p-2.5 rounded-xl ${
                  showSuggestions
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                title="Suggested questions"
              >
                <Lightbulb className="w-4 h-4" />
              </button>
              <textarea
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                placeholder={`Write to ${otherName.split(" ")[0] || otherName}…`}
                rows={1}
                data-testid="match-chat-composer"
                className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !composer.trim()}
                data-testid="match-chat-send"
                className="p-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
